import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Card, Checkbox } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { PeopleNav } from './PeopleNav';

interface ImportResult {
  created: number;
  skipped: number;
  invitations: number;
}

const EXAMPLE = 'correo;nombre completo;organización;departamento;cargo;jefe;vp\nmaria@empresa.com;María Gómez;Colliers Panamá;Contabilidad;Analista contable;jefe@empresa.com;no';

/** Importación inicial de los usuarios actuales (guía 7.5): una sola vez; después, solo por alta. Todo o nada. */
export function ImportUsersPage() {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Array<{ line: number; message: string }>>([]);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function pick(file: File | undefined) {
    setResult(null);
    setError(null);
    setLineErrors([]);
    setFileName(file?.name ?? null);
    setCsv(file ? await file.text() : '');
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setLineErrors([]);
    try {
      setResult(await api<ImportResult>('/api/usuarios/importar', { json: { csv, sendInvitations: invite } }));
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { errors?: Array<{ line: number; message: string }> } | undefined) : undefined;
      setError(errorMessage(err));
      setLineErrors(body?.errors ?? []);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('us.importar')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('us.importar_descripcion')}</p>
      </div>
      <PeopleNav />
      <Card className="max-w-3xl">
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-700">{t('us.importar_formato')}</p>
            <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs text-slate-700">{EXAMPLE}</pre>
            <p className="text-xs text-slate-500">{t('us.importar_formato_ayuda')}</p>
          </div>
          <div className="space-y-1">
            <label htmlFor="us-archivo" className="block text-sm font-medium text-slate-700">
              {t('us.importar_archivo')}
            </label>
            <input id="us-archivo" type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void pick(e.target.files?.[0])} className="text-sm" />
            {fileName && <p className="text-xs text-slate-500">{t('us.importar_lineas', { archivo: fileName, n: csv.split(/\r?\n/).filter((l) => l.trim()).length })}</p>}
          </div>
          <Checkbox checked={invite} onChange={(e) => setInvite(e.target.checked)} label={t('us.importar_invitar')} hint={t('us.importar_invitar_ayuda')} />
          {result && <Alert tone="success">{t('us.importar_ok', { creados: result.created, saltados: result.skipped, invitaciones: result.invitations })}</Alert>}
          {error && (
            <Alert>
              <p>{error}</p>
              {lineErrors.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {lineErrors.map((e) => (
                    <li key={`${e.line}-${e.message}`}>{t('us.importar_linea', { linea: e.line, motivo: e.message })}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
          <Button disabled={busy || !csv.trim()} onClick={() => void submit()}>
            {busy ? t('us.importando') : t('us.importar_boton')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
